/* ----------------------------------------------------------------------------
 * TRX "Patient Data Collection" visit form — 14 chapters
 *
 * Reuses shared chapters (BEX/BRO) and adds the TRX/COPD-specific ones.
 * Verified against the live app (USZ-TRX-00001) + firestore_backup.json:
 *   smoking:     OBJECT { habits, cigsPerDay, ended, packYears, started }
 *   exacerbation:OBJECT { previousYear, severePreviousYear }
 *   COPD qs:     flat CATScore, mMRCscore
 *   tripleEx:    quitQuestionnaire(string) + antibiotic[] + steroids[]
 *                (medication-like rows, each with a startDate)
 *   laboratory:  OBJECT { crp, crpDate, eosinophils, eosinophilsDate }
 *                + microbiology[]{report,date} + virology[]{report,date}
 *   dsi:         OBJECT { ctThorax, ctThoraxDate, emphysema, emphysemaDate,
 *                         bronchiectasis, bronchiectasisWallThick }
 * ------------------------------------------------------------------------- */
import {
  yesNo,
  anthropometrics,
  eatingDrinking,
  medication,
  morbidities,
  lungFunction,
  breathAnalysis,
} from './bex'
import { vitalSigns } from './bro'

// Medication-style row used by the antibiotic / steroid initiation groups.
const medRow = [
  {
    type: 'drug',
    name: 'drugName',
    label: 'Drug (Trade Name)',
    placeholder: 'Drug (Trade Name)',
  },
  {
    type: 'text',
    name: 'activeSubstance',
    label: 'Active Substance',
    placeholder: 'Active Substance',
  },
  {
    type: 'number',
    name: 'dosagePerIntake',
    label: 'Dosage per Intake (mg)',
    placeholder: 'Dosage',
    min: '0',
    step: 'any',
  },
  {
    type: 'number',
    name: 'intakeMorning',
    label: 'Morning',
    help: 'Morning',
    min: '0',
    value: '0',
  },
  {
    type: 'number',
    name: 'intakeMidday',
    label: 'Midday',
    help: 'Midday',
    min: '0',
    value: '0',
  },
  {
    type: 'number',
    name: 'intakeEvening',
    label: 'Evening',
    help: 'Evening',
    min: '0',
    value: '0',
  },
  {
    type: 'number',
    name: 'intakeNight',
    label: 'Night',
    help: 'Night',
    min: '0',
    value: '0',
  },
  { type: 'datetime-local', name: 'lastIntake', label: 'Last intake' },
  { type: 'date', name: 'startDate', label: 'Start date' },
]

const smoking = [
  {
    type: 'group',
    name: 'smoking',
    children: [
      {
        type: 'select',
        name: 'habits',
        label: 'Smoking habits',
        options: {
          '': '',
          neverSmoker: 'Never-smoker',
          smoker: 'Smoker',
          exSmoker: 'Ex-smoker',
        },
      },
      {
        type: 'text',
        name: 'cigsPerDay',
        label: 'Number of cigarettes per day',
      },
      { type: 'text', name: 'ended', label: 'End of smoking' },
      { type: 'text', name: 'packYears', label: 'Pack years' },
      { type: 'text', name: 'started', label: 'Start of smoking' },
    ],
  },
]

const exacerbationHistory = [
  {
    type: 'group',
    name: 'exacerbation',
    children: [
      {
        type: 'text',
        name: 'previousYear',
        label: 'Number of exacerbation previous year',
      },
      {
        type: 'text',
        name: 'severePreviousYear',
        label: 'Number of severe exacerbation previous year',
      },
    ],
  },
]

const copdQuestionnaires = [
  { type: 'text', name: 'CATScore', label: 'CAT score' },
  { type: 'text', name: 'mMRCscore', label: 'mMRC score' },
]

const tripleEx = [
  {
    type: 'select',
    name: 'quitQuestionnaire',
    label: 'QUIT questionnaire',
    options: { '': '', adherent: 'adherent', nonAdherent: 'non-adherent' },
  },
  { component: 'h4', children: 'Initiation antibiotic therapy' },
  {
    type: 'group',
    name: 'antibiotic',
    repeatable: true,
    'add-label': '+ Add antibiotic',
    children: medRow,
  },
  { component: 'h4', children: 'Initiation steroid therapy' },
  {
    type: 'group',
    name: 'steroids',
    repeatable: true,
    'add-label': '+ Add steroid',
    children: medRow,
  },
]

const microbiologyVirologyRow = [
  {
    type: 'select',
    name: 'report',
    label: 'Report',
    options: { '': '', negative: 'Negative', positive: 'Positive' },
  },
  { type: 'date', name: 'date', label: 'Date' },
]

const laboratory = [
  {
    type: 'group',
    name: 'laboratory',
    children: [
      { type: 'text', name: 'crp', label: 'CRP (mg/l)' },
      { type: 'date', name: 'crpDate', label: 'Date' },
      { type: 'text', name: 'eosinophils', label: 'Eosinophils (G/l)' },
      { type: 'date', name: 'eosinophilsDate', label: 'Date' },
    ],
  },
  { component: 'h4', children: 'Microbiology report' },
  {
    type: 'group',
    name: 'microbiology',
    repeatable: true,
    'add-label': '+ Add report',
    children: microbiologyVirologyRow,
  },
  { component: 'h4', children: 'Virology report' },
  {
    type: 'group',
    name: 'virology',
    repeatable: true,
    'add-label': '+ Add report',
    children: microbiologyVirologyRow,
  },
]

const diseaseImages = [
  {
    type: 'group',
    name: 'dsi',
    children: [
      { type: 'select', name: 'ctThorax', label: 'CT Thorax', options: yesNo },
      { type: 'date', name: 'ctThoraxDate', label: 'Date' },
      { type: 'select', name: 'emphysema', label: 'Emphysema', options: yesNo },
      { type: 'date', name: 'emphysemaDate', label: 'Date' },
      {
        type: 'select',
        name: 'bronchiectasis',
        label: 'Bronchiectasis',
        options: yesNo,
      },
      {
        type: 'select',
        name: 'bronchiectasisWallThick',
        label: 'Bronchial wall thickening',
        options: yesNo,
      },
    ],
  },
]

const finish = [{ component: 'h4', children: 'Visit 1 Completion' }]

export default {
  chapters: [
    {
      key: 'anthropometrics',
      name: 'Anthropometrics',
      schema: anthropometrics,
    },
    { key: 'vitalSigns', name: 'Vital signs', schema: vitalSigns },
    {
      key: 'eatingDrinking',
      name: 'Eating & drinking habits',
      schema: eatingDrinking,
    },
    { key: 'smokingHistory', name: 'Smoking history', schema: smoking },
    { key: 'morbidities', name: 'Morbidities', schema: morbidities },
    { key: 'medication', name: 'Medication', schema: medication },
    {
      key: 'exacerbationHistory',
      name: 'Exacerbation history',
      schema: exacerbationHistory,
    },
    {
      key: 'copdQuestionnaires',
      name: 'COPD Specific Questionnaires',
      schema: copdQuestionnaires,
    },
    { key: 'tripleExSpecifics', name: 'TripleEx specifics', schema: tripleEx },
    { key: 'laboratory', name: 'Laboratory', schema: laboratory },
    { key: 'lungFunction', name: 'Lung Function', schema: lungFunction },
    {
      key: 'diseaseImages',
      name: 'Disease specific images',
      schema: diseaseImages,
    },
    { key: 'breathAnalysis', name: 'Breath analysis', schema: breathAnalysis },
    { key: 'finish', name: 'Visit completion', schema: finish },
  ],
}
